import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  GraduationCap, 
  Search, 
  BookOpen, 
  Award, 
  Compass, 
  Cpu, 
  Briefcase, 
  Lightbulb, 
  Globe2, 
  Layers, 
  CheckCircle2,
  Filter
} from 'lucide-react';
import CaoursesTaught from "../../Media/CaoursesTaught.jpg"

const coursesData = [
  {
    id: 1,
    title: "Strategic Management",
    level: "Core MBA / Executive / Ph.D.",
    category: "Strategic Leadership",
    institution: "DMS, IIT Delhi",
    description: "Core frameworks covering competitive advantage, industry structure analysis, resource-based theory, dynamic capabilities, and strategic posture in volatile global markets.",
    focusAreas: ["Competitive Positioning", "Resource-Based View", "Dynamic Capabilities", "Strategic Execution"]
  },
  {
    id: 2,
    title: "Management of Innovation and R&D",
    level: "Advanced MBA / Doctoral",
    category: "Innovation & Technology",
    institution: "DMS, IIT Delhi",
    description: "Managing research and development portfolios, innovation pipelines, technology forecasting, disruptive architectures, and design thinking inside high-velocity industries.",
    focusAreas: ["R&D Portfolio Management", "Disruptive Innovations", "Technology Roadmapping", "Patent Strategy"]
  },
  {
    id: 3,
    title: "Management of Technology Transfer and Absorption",
    level: "Postgraduate / Executive",
    category: "Innovation & Technology",
    institution: "DMS, IIT Delhi",
    description: "Mechanisms for bilateral technology licensing, collaborative cross-border knowledge transfer, absorptive capacity, and national innovation system integration.",
    focusAreas: ["Cross-Border Licensing", "Absorptive Capacity", "Bilateral Tech Agreements", "Commercialization"]
  },
  {
    id: 4,
    title: "Creative Problem Solving",
    level: "MBA / Management Development",
    category: "Strategic Leadership",
    institution: "DMS, IIT Delhi",
    description: "Heuristic methodologies, TRIZ frameworks, lateral thinking models, and structured cognitive reframing to overcome organizational gridlocks and legacy biases.",
    focusAreas: ["TRIZ Problem Solving", "Heuristic Search", "Cognitive Reframing", "Lateral Thinking"]
  },
  {
    id: 5,
    title: "Fundamentals of Management of Technology",
    level: "Core Engineering & Management",
    category: "Innovation & Technology",
    institution: "DMS, IIT Delhi",
    description: "Foundational paradigms connecting technological life cycles, modular product architecture, standard wars, and S-curves to operational competitiveness.",
    focusAreas: ["Tech Life Cycles", "S-Curves & Standard Wars", "Modular Product Design", "Process Optimization"]
  },
  {
    id: 6,
    title: "Strategic Consulting",
    level: "Advanced MBA Elective",
    category: "Consulting Practice",
    institution: "DMS, IIT Delhi",
    description: "Structuring ambiguity, hypothesis-driven problem identification, stakeholder management, diagnostic pyramids, and delivering board-level transformation roadmaps.",
    focusAreas: ["Hypothesis Generation", "Issue Trees & Pyramids", "Client Diagnostics", "Change Management"]
  },
  {
    id: 7,
    title: "Consulting Toolkit",
    level: "Executive & Professional Practice",
    category: "Consulting Practice",
    institution: "DMS, IIT Delhi",
    description: "Hands-on diagnostic frameworks, qualitative and quantitative benchmarking, scenario generation models, and C-suite presentation delivery.",
    focusAreas: ["Analytical Toolkits", "Benchmarking Matrices", "Financial Diagnostic Models", "Executive Synthesis"]
  },
  {
    id: 8,
    title: "Strategic Alliance",
    level: "Postgraduate / Doctoral",
    category: "Global Strategy & Alliances",
    institution: "DMS, IIT Delhi",
    description: "Formation motives, partner fit, joint venture governance, ambidexterity, termination dynamics, and value creation in emerging market alliances.",
    focusAreas: ["Joint Venture Governance", "Interpartner Fit", "Termination Dynamics", "Alliance Networks"]
  },
  {
    id: 9,
    title: "Current and Emerging Issues in Strategic Management",
    level: "Advanced Doctoral / Executive",
    category: "Strategic Leadership",
    institution: "DMS, IIT Delhi",
    description: "Contemporary frontiers including AI governance, digital transformation ethics, platform ecosystems, geopolitics, and sustainability net-zero mandates.",
    focusAreas: ["AI Governance", "Platform Ecosystems", "Net-Zero Strategies", "Geopolitical Volatility"]
  },
  {
    id: 10,
    title: "International Business",
    level: "Core MBA",
    category: "Global Strategy & Alliances",
    institution: "DMS, IIT Delhi",
    description: "Global market entry modalities, psychic distance, institutional voids, cross-border M&A, and managing multinational enterprises in multilateral environments.",
    focusAreas: ["Entry Mode Strategy", "Institutional Theory", "Cross-Border M&A", "Global Value Chains"]
  },
  {
    id: 11,
    title: "Small Business Strategy",
    level: "MBA / Entrepreneurship",
    category: "Strategic Leadership",
    institution: "DMS, IIT Delhi",
    description: "Entrepreneurial bootstrapping, bricolage, scaling constraints, resource orchestration, and strategic resilience for startups and MSME clusters.",
    focusAreas: ["Bootstrapping & Bricolage", "Cluster Competitiveness", "Cash Flow Resilience", "Scaling Milestones"]
  }
];

const categories = ["All", "Strategic Leadership", "Innovation & Technology", "Consulting Practice", "Global Strategy & Alliances"];

export default function CoursesTaught() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredCourses = useMemo(() => {
    return coursesData.filter((course) => {
      const matchesCategory = selectedCategory === "All" || course.category === selectedCategory;
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        course.title.toLowerCase().includes(q) ||
        course.description.toLowerCase().includes(q) ||
        course.focusAreas.some((tag) => tag.toLowerCase().includes(q)) ||
        course.level.toLowerCase().includes(q);

      return matchesCategory && matchesSearch;
    });
  }, [searchTerm, selectedCategory]);

  return (
    <div className="container py-5 min-vh-100 position-relative text-light" style={{ zIndex: 1 }}>
      
      {/* 1. Header Banner */}
      <div className="row align-items-center g-4 mb-5">
        <div className="col-12 col-lg-8" data-aos="fade-right">
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3 sub-badge">
            <GraduationCap size={16} className="text-cyan" />
            <span className="small text-light fw-medium">Teaching & Academic Pedagogy</span>
          </div>

          <h1 className="display-5 fw-bold text-white mb-3">
            Courses <span className="text-gradient">Taught</span>
          </h1>
            <img src={CaoursesTaught} alt="SanjayDhir" width={500} className='rounded-4 m-3'/>
          <p className="lead text-secondary fs-6 mb-4 pe-lg-3">
            Graduate, doctoral, and executive leadership curricula delivered at the 
            <strong> Department of Management Studies, IIT Delhi</strong>[cite: 2]. Integrating theoretical rigor 
            with empirical case research and practical toolkits for strategic decision-makers[cite: 2].
          </p>

          {/* Quick Metrics Bar */}
          <div className="d-flex flex-wrap gap-3">
            <div className="p-3 rounded-3 glass-card-sm text-center" style={{ minWidth: '110px' }}>
              <span className="fs-4 fw-bold text-cyan">11+</span>
              <span className="small text-secondary d-block" style={{ fontSize: '0.75rem' }}>Specialized Courses</span>
            </div>
            <div className="p-3 rounded-3 glass-card-sm text-center" style={{ minWidth: '110px' }}>
              <span className="fs-4 fw-bold text-warning">MBA & Ph.D.</span>
              <span className="small text-secondary d-block" style={{ fontSize: '0.75rem' }}>Academic Levels</span>
            </div>
            <div className="p-3 rounded-3 glass-card-sm text-center" style={{ minWidth: '110px' }}>
              <span className="fs-4 fw-bold text-info">IIT Delhi</span>
              <span className="small text-secondary d-block" style={{ fontSize: '0.75rem' }}>Teaching Excellence[cite: 2]</span>
            </div>
          </div>
        </div>

        {/* Pedagogy Philosophy Card */}
        <div className="col-12 col-lg-4" data-aos="fade-left">
          <div className="p-4 rounded-4 glass-panel border-glow text-center d-flex flex-column align-items-center justify-content-center h-100">
            <Compass size={42} className="text-cyan mb-3" />
            <h5 className="text-white fw-bold mb-2">Applied Strategy Pedagogy</h5>
            <p className="text-secondary small mb-0 lh-base">
              Awarded the <strong>Teaching Excellence Award</strong> at IIT Delhi and 
              recognized two years consecutively as <strong>InsideIIM Professor of the Year</strong>[cite: 2].
            </p>
          </div>
        </div>
      </div>

      {/* 2. Search & Filter Bar */}
      <div className="p-4 rounded-4 glass-panel mb-5" data-aos="fade-up">
        <div className="row g-3 align-items-center justify-content-between">
          
          {/* Search Box */}
          <div className="col-12 col-md-5">
            <div className="position-relative">
              <Search size={18} className="text-secondary position-absolute top-50 start-0 translate-middle-y ms-3" />
              <input
                type="text"
                placeholder="Search courses by topic, keyword, or framework..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control rounded-pill ps-5 bg-dark text-light border border-secondary border-opacity-50 py-2 small"
              />
            </div>
          </div>

          {/* Result Count */}
          <div className="col-12 col-md-auto text-md-end text-secondary small">
            Showing <strong className="text-white">{filteredCourses.length}</strong> of {coursesData.length} courses
          </div>
        </div>

        {/* Filter Badges with explicit highlight styling */}
        <div className="d-flex flex-wrap gap-2 mt-3 pt-3 border-top border-secondary border-opacity-25 align-items-center">
          <div className="d-flex align-items-center gap-1 text-secondary small me-2">
            <Filter size={14} /> Domain:
          </div>
          {categories.map((cat, idx) => {
            const isSelected = selectedCategory === cat;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className="btn btn-sm rounded-pill px-3 py-1 transition-all"
                style={{
                  fontSize: '0.8rem',
                  fontWeight: isSelected ? '700' : '500',
                  color: isSelected ? '#0a0f1d' : '#94a3b8',
                  background: isSelected ? 'linear-gradient(135deg, #00f2fe, #4facfe)' : 'rgba(255, 255, 255, 0.05)',
                  border: isSelected ? '1px solid #00f2fe' : '1px solid rgba(255, 255, 255, 0.15)',
                  boxShadow: isSelected ? '0 0 14px rgba(0, 242, 254, 0.45)' : 'none'
                }}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      {/* 3. Responsive Course Cards Grid */}
      <div className="row g-4">
        <AnimatePresence>
          {filteredCourses.map((item, index) => (
            <motion.div
              layout
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.25 }}
              key={item.id}
              className="col-12 col-lg-6"
            >
              <div 
                className="p-4 rounded-4 glass-panel h-100 d-flex flex-column justify-content-between position-relative overflow-hidden"
                style={{
                  borderLeft: '4px solid #00f2fe'
                }}
              >
                <div>
                  {/* Category & Academic Level Header */}
                  <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                    <span 
                      className="badge px-3 py-1 rounded-pill small"
                      style={{
                        background: 'rgba(0, 242, 254, 0.08)',
                        color: '#00f2fe',
                        border: '1px solid rgba(0, 242, 254, 0.2)',
                        fontSize: '0.72rem'
                      }}
                    >
                      {item.category}
                    </span>

                    <span className="small text-warning fw-semibold d-inline-flex align-items-center gap-1">
                      <BookOpen size={13} /> {item.level}
                    </span>
                  </div>

                  {/* Course Title */}
                  <h5 className="text-white fw-bold fs-5 mb-2 lh-base">
                    {item.title}
                  </h5>

                  {/* Course Description */}
                  <p className="text-light opacity-90 small mb-3 lh-base">
                    {item.description}
                  </p>

                  {/* Key Topic Badges */}
                  <div className="d-flex flex-wrap gap-1 mb-3">
                    {item.focusAreas.map((tag, tIdx) => (
                      <span 
                        key={tIdx} 
                        className="badge bg-dark text-secondary border border-secondary border-opacity-25 px-2 py-1"
                        style={{ fontSize: '0.7rem', fontWeight: '500' }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Footer Institution Tag */}
                <div className="pt-3 border-top border-secondary border-opacity-15 d-flex align-items-center justify-content-between gap-2 text-secondary small">
                  <div className="d-inline-flex align-items-center gap-1 text-light">
                    <CheckCircle2 size={14} className="text-cyan flex-shrink-0" />
                    <span>Department of Management Studies (DMS)</span>
                  </div>
                  <span className="badge bg-secondary bg-opacity-25 text-info border border-secondary border-opacity-50" style={{ fontSize: '0.7rem' }}>
                    IIT Delhi
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {filteredCourses.length === 0 && (
          <div className="col-12 text-center py-5">
            <p className="text-secondary fs-5 mb-0">No courses match your search criteria.</p>
          </div>
        )}
      </div>

    </div>
  );
}